import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../AdminPanel/Sidebar";
import TopBar from "../AdminPanel/TopBar";
import ProductDetail from "./ProductDetail";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

function formatMoney(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney6(value) {
  const number = Number(value || 0);

  return number.toLocaleString("es-EC", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });
}

function round2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function round6(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 1000000) / 1000000;
}

function normalizeIva(value) {
  const iva = Number(value || 0);

  if (iva === 999) return 999;
  if ([0, 5, 15].includes(iva)) return iva;

  return 0;
}

function getFinalFromBase(base, ivaPorcentaje) {
  const precioBase = Number(base || 0);
  const iva = normalizeIva(ivaPorcentaje);

  if (iva === 999 || iva <= 0) {
    return round6(precioBase);
  }

  return round6(precioBase + precioBase * (iva / 100));
}

function calculateLineFromBase(precioSinIva, ivaPorcentaje, cantidad) {
  const baseUnitario = Number(precioSinIva || 0);
  const iva = normalizeIva(ivaPorcentaje);
  const qty = Number(cantidad || 0);
  const totalSinIva = round6(baseUnitario * qty);
  const precioConIva = getFinalFromBase(baseUnitario, iva);

  if (iva === 999 || iva <= 0) {
    return {
      precioSinIva: round6(baseUnitario),
      precioConIva,
      ivaUnitario: 0,
      totalSinIva,
      ivaTotal: 0,
      total: totalSinIva,
    };
  }

  const ivaTotal = round6(totalSinIva * (iva / 100));
  const total = round6(totalSinIva + ivaTotal);

  return {
    precioSinIva: round6(baseUnitario),
    precioConIva,
    ivaUnitario: qty > 0 ? round6(ivaTotal / qty) : 0,
    totalSinIva,
    ivaTotal,
    total,
  };
}

function getFirstNumber(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== "") {
      return Number(value || 0);
    }
  }

  return 0;
}

export default function NewOrden() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { dashboardTheme } = useDashboardTheme();
  const { showError, showSuccess } = useAlert();
  const navigate = useNavigate();
  const location = useLocation();

  const token = localStorage.getItem("accessToken");
  const ordenFromState = location.state?.orden || null;

  const [loading, setLoading] = useState(false);
  const [ordenCreada, setOrdenCreada] = useState(ordenFromState);

  const [clienteReferencia, setClienteReferencia] = useState(
    ordenFromState?.cliente_referencia || ""
  );
  const [mesa, setMesa] = useState(ordenFromState?.mesa || "");
  const [detalles, setDetalles] = useState([]);

  const canEditOrden = !ordenCreada?.cobrada && !ordenCreada?.facturada;
  const isSaved = !!ordenCreada?.id;

  const normalizeDetalleFromBackend = (detalle) => {
    const cantidad = getFirstNumber(detalle.cantidad);
    const ivaPorcentaje = normalizeIva(detalle.iva_porcentaje);
    const precioSinIva = getFirstNumber(detalle.precio_unitario, detalle.precio_sin_iva);
    const calculo = calculateLineFromBase(precioSinIva, ivaPorcentaje, cantidad);

    const totalSinIva = getFirstNumber(
      detalle.total_sin_iva,
      detalle.subtotal,
      detalle.base_imponible,
      calculo.totalSinIva
    );

    const ivaTotal = getFirstNumber(
      detalle.iva_total,
      detalle.iva_valor,
      calculo.ivaTotal
    );

    const total = getFirstNumber(
      detalle.total,
      round6(totalSinIva + ivaTotal)
    );

    return {
      uid: `backend-${detalle.id}`,
      backend_id: detalle.id,
      producto: detalle.producto || detalle.producto_id,
      producto_id: detalle.producto || detalle.producto_id,
      producto_nombre: detalle.producto_nombre || detalle.descripcion || "",
      descripcion: detalle.descripcion || detalle.producto_nombre || "",
      cantidad: round6(cantidad),
      precio_unitario: round6(precioSinIva),
      precio_sin_iva: round6(precioSinIva),
      precio_con_iva: calculo.precioConIva,
      iva_porcentaje: ivaPorcentaje,
      iva_unitario: cantidad > 0 ? round6(ivaTotal / cantidad) : 0,
      total_sin_iva: round6(totalSinIva),
      iva_total: round6(ivaTotal),
      total: round6(total),
      observacion: detalle.observacion || "",
      sabores: detalle.sabores || [],
      sabores_ids: (detalle.sabores || []).map((item) => item.sabor || item.sabor_id || item.id),
    };
  };

  const loadDetalleOrden = async (ordenId) => {
    if (!ordenId) return;

    try {
      setLoading(true);

      const response = await fetch(`/api/ventas/ordenes/${ordenId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo cargar la orden");
        return;
      }

      const orden = data.orden || null;

      setOrdenCreada(orden);
      setClienteReferencia(orden?.cliente_referencia || "");
      setMesa(orden?.mesa || "");
      setDetalles((orden?.detalles || []).map(normalizeDetalleFromBackend));
    } catch {
      showError("Error de conexión cargando orden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (ordenFromState?.id) {
      loadDetalleOrden(ordenFromState.id);
    }
  }, []);

  const resumen = useMemo(() => {
    const data = {
      subtotalSinIva: 0,
      baseIva0: 0,
      baseIva5: 0,
      baseIva15: 0,
      baseNoObjeto: 0,
      iva5: 0,
      iva15: 0,
      ivaTotal: 0,
      total: 0,
    };

    detalles.forEach((detalle) => {
      const totalSinIva = Number(detalle.total_sin_iva || 0);
      const ivaTotal = Number(detalle.iva_total || 0);
      const total = Number(detalle.total || 0);
      const ivaPorcentaje = normalizeIva(detalle.iva_porcentaje);

      data.subtotalSinIva += totalSinIva;
      data.ivaTotal += ivaTotal;
      data.total += total;

      if (ivaPorcentaje === 5) {
        data.baseIva5 += totalSinIva;
        data.iva5 += ivaTotal;
      } else if (ivaPorcentaje === 15) {
        data.baseIva15 += totalSinIva;
        data.iva15 += ivaTotal;
      } else if (ivaPorcentaje === 999) {
        data.baseNoObjeto += totalSinIva;
      } else {
        data.baseIva0 += totalSinIva;
      }
    });

    data.subtotalSinIva = round2(data.subtotalSinIva);
    data.baseIva0 = round2(data.baseIva0);
    data.baseIva5 = round2(data.baseIva5);
    data.baseIva15 = round2(data.baseIva15);
    data.baseNoObjeto = round2(data.baseNoObjeto);
    data.iva5 = round2(data.iva5);
    data.iva15 = round2(data.iva15);
    data.ivaTotal = round2(data.ivaTotal);
    data.total = round2(data.total);

    return data;
  }, [detalles]);

  const validateHeader = () => {
    if (!clienteReferencia.trim() && !mesa.trim()) {
      showError("Ingresa el nombre/referencia del cliente o la mesa.");
      return false;
    }

    return true;
  };

  const handleAddDetail = (detalle) => {
    setDetalles((prev) => [
      ...prev,
      {
        ...detalle,
        cantidad: round6(detalle.cantidad),
        precio_unitario: round6(detalle.precio_unitario),
        precio_sin_iva: round6(detalle.precio_sin_iva || detalle.precio_unitario),
        precio_con_iva: round6(detalle.precio_con_iva),
        iva_unitario: round6(detalle.iva_unitario),
        total_sin_iva: round6(detalle.total_sin_iva),
        iva_total: round6(detalle.iva_total),
        total: round6(detalle.total),
      },
    ]);
  };

  const eliminarDetalleLocal = async (detalle) => {
    if (!detalle?.backend_id) {
      setDetalles((prev) => prev.filter((item) => item.uid !== detalle.uid));
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(`/api/ventas/ordenes/detalle/${detalle.backend_id}/eliminar/`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        showError(data.error || data.detail || "No se pudo eliminar el producto");
        return;
      }

      showSuccess(data.mensaje || "Producto eliminado");
      setDetalles((prev) => prev.filter((item) => item.uid !== detalle.uid));

      if (data.orden) {
        setOrdenCreada(data.orden);
      }
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const crearOrden = async () => {
    if (!validateHeader()) return;

    if (!detalles.length) {
      showError("Agrega al menos un producto a la orden.");
      return;
    }

    try {
      setLoading(true);

      let orden = ordenCreada;

      if (!orden?.id) {
        const response = await fetch("/api/ventas/ordenes/crear/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            cliente_referencia: clienteReferencia.trim(),
            mesa: mesa.trim(),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          showError(data.error || data.detail || "No se pudo crear la orden");
          return;
        }

        orden = data.orden || null;
      } else {
        const response = await fetch(`/api/ventas/ordenes/${orden.id}/`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            cliente_referencia: clienteReferencia.trim(),
            mesa: mesa.trim(),
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          showError(data.error || data.detail || "No se pudo actualizar la orden");
          return;
        }

        orden = data.orden || orden;
      }

      if (!orden?.id) {
        showError("No se pudo obtener la orden creada.");
        return;
      }

      let ultimaOrden = orden;
      const pendientes = detalles.filter((detalle) => !detalle.backend_id);

      for (const detalle of pendientes) {
        const responseDetalle = await fetch(`/api/ventas/ordenes/${orden.id}/agregar-detalle/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            producto: detalle.producto_id || detalle.producto,
            cantidad: String(round6(detalle.cantidad || 0)),
            descripcion: detalle.descripcion || detalle.producto_nombre || "",
            precio_unitario: String(round6(detalle.precio_sin_iva || detalle.precio_unitario || 0)),
            iva_porcentaje: String(detalle.iva_porcentaje || 0),
            total_sin_iva: String(round6(detalle.total_sin_iva || 0)),
            iva_total: String(round6(detalle.iva_total || 0)),
            total: String(round6(detalle.total || 0)),
            observacion: detalle.observacion || "",
            sabores: detalle.sabores_ids || [],
          }),
        });

        const dataDetalle = await responseDetalle.json();

        if (!responseDetalle.ok) {
          showError(dataDetalle.error || dataDetalle.detail || "No se pudo guardar un producto de la orden");
          return;
        }

        ultimaOrden = dataDetalle.orden || ultimaOrden;
      }

      showSuccess(isSaved ? "Orden actualizada correctamente" : "Orden creada correctamente");
      setOrdenCreada(ultimaOrden);

      if (ultimaOrden?.id) {
        await loadDetalleOrden(ultimaOrden.id);
      }
    } catch {
      showError("Error de conexión");
    } finally {
      setLoading(false);
    }
  };

  const facturarOrden = () => {
    if (!ordenCreada?.id) {
      showError("Primero crea la orden.");
      return;
    }

    if (!detalles.length) {
      showError("La orden no tiene productos para facturar.");
      return;
    }

    if (ordenCreada?.facturada) {
      showError("Esta orden ya fue facturada.");
      return;
    }

    navigate(`/admin-panel/facturacion/nueva?orden=${ordenCreada.id}`, {
      state: {
        orden: ordenCreada,
      },
    });
  };

  const imprimirOrden = async () => {
    if (!ordenCreada?.id) {
      showError("Primero crea la orden.");
      return;
    }

    if (!detalles.length) {
      showError("La orden no tiene productos para imprimir.");
      return;
    }

    try {
      setLoading(true);

      const ordenParaImprimir = {
        id: ordenCreada.id,
        numero: ordenCreada.numero || ordenCreada.id,
        cliente_referencia: clienteReferencia || ordenCreada.cliente_referencia || "",
        mesa: mesa || ordenCreada.mesa || "",
        detalles: detalles.map((detalle) => ({
          descripcion: detalle.descripcion || detalle.producto_nombre || "",
          producto_nombre: detalle.producto_nombre || detalle.descripcion || "",
          cantidad: detalle.cantidad,
          observacion: detalle.observacion || "",
          sabores: detalle.sabores || [],
        })),
      };

      const response = await fetch("http://127.0.0.1:5050/print-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(ordenParaImprimir),
      });

      const data = await response.json();

      if (!response.ok || !data.ok) {
        showError(data.error || "No se pudo imprimir la orden.");
        return;
      }

      showSuccess("Orden enviada a impresión.");
    } catch {
      showError("No se pudo conectar con el puente de impresión local.");
    } finally {
      setLoading(false);
    }
  };

  const outlinedButtonSx = {
    minHeight: 40,
    px: 2.4,
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const smallButtonSx = {
    minHeight: 34,
    px: 1.6,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  const cardSx = {
    p: { xs: 1.5, sm: 2, md: 2.5 },
    borderRadius: "14px",
    border: `1px solid ${dashboardTheme.border_color}`,
    bgcolor: dashboardTheme.card_background,
    boxShadow: "none",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: dashboardTheme.dashboard_background,
        display: "flex",
        color: dashboardTheme.text_color,
        overflowX: "hidden",
      }}
    >
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
        />

        <Box
          component="main"
          sx={{
            flex: 1,
            width: "100%",
            p: { xs: 1.5, sm: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", lg: "95%" },
              mx: "auto",
              mt: { xs: 1, md: 1.5 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                mb: { xs: 1.5, md: 2 },
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: 24, sm: 26, md: 30 },
                  fontWeight: 600,
                  color: dashboardTheme.primary_color,
                  lineHeight: 1.2,
                }}
              >
                {ordenCreada?.numero ? `Orden #${ordenCreada.numero}` : "Nueva orden"}
              </Typography>

              <Button
                variant="outlined"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate("/admin-panel/ordenes")}
                sx={outlinedButtonSx}
              >
                Volver
              </Button>
            </Box>

            <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
              <Box sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
                <Box sx={cardSx}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                    <ReceiptLongRoundedIcon sx={{ color: dashboardTheme.primary_color }} />

                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                      }}
                    >
                      Datos de la orden
                    </Typography>
                  </Stack>

                  <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Cliente o referencia"
                      value={clienteReferencia}
                      onChange={(e) => setClienteReferencia(e.target.value)}
                      disabled={!canEditOrden}
                      sx={dashboardTheme.inputStyles}
                    />

                    <TextField
                      size="small"
                      label="Mesa"
                      value={mesa}
                      onChange={(e) => setMesa(e.target.value)}
                      disabled={!canEditOrden}
                      sx={{
                        minWidth: { xs: "100%", md: 160 },
                        ...dashboardTheme.inputStyles,
                      }}
                    />
                  </Stack>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <ProductDetail onAddDetail={handleAddDetail} disabled={loading || !canEditOrden} />
                </Box>
              </Box>

              <Box sx={dashboardTheme.tableStyles.tableArea}>
                <TableContainer sx={dashboardTheme.tableStyles.container}>
                  <Table size="small" sx={dashboardTheme.tableStyles.table}>
                    <TableHead>
                      <TableRow sx={dashboardTheme.tableStyles.headRow}>
                        <TableCell sx={{ ...dashboardTheme.tableStyles.headCell, minWidth: 240 }}>
                          Producto
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Cant.
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Sabores
                        </TableCell>

                        <TableCell sx={dashboardTheme.tableStyles.headCell}>
                          Detalle
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Precio Unit.
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Subtotal
                        </TableCell>

                        <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>
                          Acciones
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {detalles.map((detalle) => (
                        <TableRow key={detalle.uid} hover sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: dashboardTheme.text_color,
                                lineHeight: 1.25,
                              }}
                            >
                              {detalle.descripcion || detalle.producto_nombre || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            {detalle.cantidad}
                          </TableCell>

                          <TableCell>
                            {(detalle.sabores || [])
                              .map((item) => item.sabor_nombre || item.nombre)
                              .filter(Boolean)
                              .join(", ") || "-"}
                          </TableCell>

                          <TableCell>{detalle.observacion || "-"}</TableCell>

                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            ${formatMoney6(detalle.precio_sin_iva || detalle.precio_unitario)}
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            ${formatMoney6(detalle.total_sin_iva)}
                          </TableCell>

                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DeleteRoundedIcon />}
                              onClick={() => eliminarDetalleLocal(detalle)}
                              disabled={loading || !canEditOrden}
                              sx={smallButtonSx}
                            >
                              Quitar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}

                      {detalles.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            align="center"
                            sx={dashboardTheme.tableStyles.emptyCell}
                          >
                            Selecciona productos desde el buscador y añádelos a la tabla
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box
                sx={{
                  p: { xs: 1.5, sm: 2, md: 2.5 },
                  borderTop: `1px solid ${dashboardTheme.border_color}`,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <Box
                  sx={{
                    width: { xs: "100%", sm: 420 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.4,
                  }}
                >
                  <Box
                    sx={{
                      borderRadius: "14px",
                      border: `1px solid ${dashboardTheme.border_color}`,
                      p: 2,
                      bgcolor: dashboardTheme.card_background,
                    }}
                  >
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                          Subtotal sin impuestos
                        </Typography>
                        <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                          ${formatMoney(resumen.subtotalSinIva)}
                        </Typography>
                      </Stack>

                      {resumen.baseIva0 > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                            Subtotal IVA 0%
                          </Typography>
                          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                            ${formatMoney(resumen.baseIva0)}
                          </Typography>
                        </Stack>
                      )}

                      {resumen.baseNoObjeto > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                            Subtotal no objeto IVA
                          </Typography>
                          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                            ${formatMoney(resumen.baseNoObjeto)}
                          </Typography>
                        </Stack>
                      )}

                      {resumen.baseIva5 > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                            Subtotal IVA 5%
                          </Typography>
                          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                            ${formatMoney(resumen.baseIva5)}
                          </Typography>
                        </Stack>
                      )}

                      {resumen.iva5 > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                            IVA 5%
                          </Typography>
                          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                            ${formatMoney(resumen.iva5)}
                          </Typography>
                        </Stack>
                      )}

                      {resumen.baseIva15 > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                            Subtotal IVA 15%
                          </Typography>
                          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                            ${formatMoney(resumen.baseIva15)}
                          </Typography>
                        </Stack>
                      )}

                      {resumen.iva15 > 0 && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                            IVA 15%
                          </Typography>
                          <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                            ${formatMoney(resumen.iva15)}
                          </Typography>
                        </Stack>
                      )}

                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: dashboardTheme.muted_text_color, fontSize: 14 }}>
                          Valor total IVA
                        </Typography>
                        <Typography sx={{ color: dashboardTheme.text_color, fontSize: 14, fontWeight: 600 }}>
                          ${formatMoney(resumen.ivaTotal)}
                        </Typography>
                      </Stack>

                      <Divider sx={{ borderColor: dashboardTheme.border_color }} />

                      <Stack direction="row" justifyContent="space-between">
                        <Typography sx={{ color: dashboardTheme.text_color, fontSize: 18, fontWeight: 700 }}>
                          Valor total
                        </Typography>
                        <Typography sx={{ color: dashboardTheme.primary_color, fontSize: 18, fontWeight: 700 }}>
                          ${formatMoney(resumen.total)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SaveRoundedIcon />}
                      onClick={crearOrden}
                      disabled={loading || !canEditOrden}
                      sx={dashboardTheme.buttonStyles.contained}
                    >
                      {isSaved ? "Actualizar" : "Crear orden"}
                    </Button>

                    {isSaved && (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<PrintRoundedIcon />}
                        onClick={imprimirOrden}
                        disabled={loading || !detalles.length}
                        sx={outlinedButtonSx}
                      >
                        Imprimir
                      </Button>
                    )}

                    {isSaved && (
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ReceiptLongRoundedIcon />}
                        onClick={facturarOrden}
                        disabled={loading || !detalles.length || ordenCreada?.facturada}
                        sx={outlinedButtonSx}
                      >
                        Facturar
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}